
import React from 'react';
import { PricingPlan } from '@/config/pricingPlans';
import PricingCard from '@/components/PricingCard';
import ModelComparisonTable from '@/components/ModelComparisonTable';
import { ShieldCheck } from 'lucide-react';

interface PricingSectionProps {
  plans: PricingPlan[];
  models: any[];
  isLoading: boolean;
  onPlanClick: (planId: string) => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({ plans, models, isLoading, onPlanClick }) => {
  return (
    <section id="pricing" className="py-16 px-4 bg-gradient-to-b from-white to-muted/20">
      <div className="container mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose Your Perfect Plan</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Access multiple AI models with different compute credit allocations based on your needs
          </p>
          
          {/* Money-back guarantee section */}
          <div className="flex items-center justify-center gap-2 mt-4 text-green-600">
            <ShieldCheck className="h-6 w-6" />
            <p className="text-sm font-medium">
              All plans include our 7-day money-back guarantee
            </p>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-krix-purple"></div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row justify-center gap-8 mb-16">
            {plans.map((plan) => (
              <PricingCard
                key={plan.id}
                title={plan.title}
                description={plan.description}
                price={plan.price}
                originalPrice={plan.originalPrice}
                priceDetail={plan.priceDetail}
                features={plan.features}
                highlight={plan.highlight}
                badge={plan.badge}
                buttonText={`Get ${plan.title}`}
                onButtonClick={() => onPlanClick(plan.id)}
                isIndia={true}
                computeCredits={plan.computeCredits}
                discountPercentage={plan.discountPercentage}
              />
            ))}
          </div>
        )}
        
        <p className="text-center text-sm text-muted-foreground mb-20">
          Launch pricing is locked in for as long as you maintain your subscription
        </p>
        
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 max-w-5xl mx-auto mb-12">
          <h3 className="text-2xl font-bold text-center mb-4 sm:mb-8">Available Models By Plan</h3>
          <p className="text-center text-muted-foreground mb-6 max-w-2xl mx-auto">
            Compare which AI models are included in each plan. Upgrade anytime to access more premium models.
          </p>
          
          <ModelComparisonTable models={models} />
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
